/**
 * MCP hooks for React mesh apps.
 *
 *   - useMcpRegistry()   — fetches `/.well-known/mcp/registry.json` once
 *                          and caches it (localStorage + module-level
 *                          inflight dedupe).
 *   - useMcpAttachments() — drives the discover/detach lifecycle against
 *                          a `ToolRegistry`. Hands back the live list +
 *                          per-URL status (idle/connecting/attached/error).
 *
 * The host decides which URLs to attach (the persona's `mcpEndpoints`).
 * The hook does the attach + cleanup; it does NOT decide policy.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { discoverMcpEndpoint, detachMcpEndpoint, fetchMcpRegistry, } from '@unstable-legion/core';
export function useMcpRegistry() {
    const [registry, setRegistry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchMcpRegistry()
            .then((r) => setRegistry(r))
            .catch((err) => setError(err instanceof Error ? err.message : String(err)))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    return { registry, loading, error, reload: load };
}
export function useMcpAttachments(opts) {
    const { registry, urls, proxyBaseUrl } = opts;
    const [statuses, setStatuses] = useState(() => new Map());
    // Keep a ref so the per-URL effect can read latest state.
    const statusesRef = useRef(statuses);
    useEffect(() => {
        statusesRef.current = statuses;
    }, [statuses]);
    // The set of URLs we've already kicked off attach for — avoids re-firing
    // on every render. Re-attach happens only if the URL is dropped + re-
    // added or an explicit `attach()` call.
    const attachInflight = useRef(new Set());
    const setStatus = useCallback((url, next) => {
        setStatuses((prev) => {
            const m = new Map(prev);
            m.set(url, next);
            return m;
        });
    }, []);
    const attach = useCallback(async (url) => {
        if (attachInflight.current.has(url))
            return;
        attachInflight.current.add(url);
        setStatus(url, { phase: 'connecting' });
        try {
            const attachment = await discoverMcpEndpoint(url, registry, { proxyBaseUrl });
            setStatus(url, { phase: 'attached', attachment });
        }
        catch (err) {
            const typed = err ?? {
                kind: 'network',
                url,
                detail: err instanceof Error ? err.message : String(err),
            };
            setStatus(url, { phase: 'error', error: typed });
        }
        finally {
            attachInflight.current.delete(url);
        }
    }, [registry, setStatus, proxyBaseUrl]);
    const detach = useCallback((url) => {
        const status = statusesRef.current.get(url);
        if (status?.phase === 'attached') {
            detachMcpEndpoint(status.attachment, registry);
        }
        setStatuses((prev) => {
            const m = new Map(prev);
            m.delete(url);
            return m;
        });
    }, [registry]);
    // Diff: ensure every URL in `urls` has been attached; drop ones removed.
    useEffect(() => {
        const want = new Set(urls);
        for (const have of statusesRef.current.keys()) {
            if (!want.has(have))
                detach(have);
        }
        for (const u of urls) {
            const cur = statusesRef.current.get(u);
            if (!cur || cur.phase === 'idle') {
                void attach(u);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(urls)]);
    // Derive convenience lists.
    const attached = [];
    const attachedTools = [];
    for (const [, status] of statuses) {
        if (status.phase === 'attached') {
            attached.push(status.attachment);
            for (const name of status.attachment.registeredNames) {
                attachedTools.push({ url: status.attachment.url, toolName: name });
            }
        }
    }
    return { statuses, attached, attachedTools, attach, detach };
}
